import { HttpEvent, HttpHandler, HttpRequest, HttpInterceptor } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthApiService } from '@core/services/auth/auth-api.service';
import { isNativePlatform } from 'src/app/_core/utils/util';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Description of this file...
 * JwtInterceptor - Automatically Attaches Authentication Headers
 *
 * This interceptor runs on EVERY outgoing HTTP request and adds:
 * 1. Authorization header with the JWT token (Bearer token)
 * 2. X-Client-ID header for multi-tenant support
 *
 * Why use an interceptor?
 * - DRY (Don't Repeat Yourself): Without this, every service method would need
 *   to manually add headers
 * - Centralized: Easy to modify authentication logic in one place
 * - Automatic: Developers don't need to remember to add headers
 *
 * How it works:
 * 1. Angular's HttpClient calls this interceptor before sending any request
 * 2. We check if the request is going to our API (not external APIs)
 * 3. If we have a token, we clone the request and add headers
 * 4. The modified request continues to the server
 */

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private authApiService = inject(AuthApiService);
  //private platformService = inject(PlatformService);

  //private readonly isMobile = this.platformService.isNativePlatform();

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const token = this.authApiService.getToken();
    const isApiRequest = request.url.startsWith(environment.apiUrl);

    // Clone the request and add headers
    if (isApiRequest) {
      const headersToAdd: Record<string, string> = {};

      if (isNativePlatform()) {
        const clientId = this.authApiService.getClientId();
        if (clientId) {
          headersToAdd['X-Client-ID'] = clientId;
        }
      }
      // Add Authorization header only if token exists
      if (token) {
        headersToAdd['Authorization'] = `Bearer ${token}`;
      }

      request = request.clone({
        withCredentials: true,
        setHeaders: headersToAdd,
      });
    }

    return next.handle(request);
  }
}
